/**
 * Sales ledger (Faraz brief, 2026-08-25: "a page to show all amazon sales,
 * with all sorting, filters, use the complete real estate, no sorry rows").
 * React-query hook for GET /api/sales, plus every pure helper the page
 * needs (default date range, dynamic status pills, search, sort, badge
 * mapping, totals, empty-state/cap-notice copy) — unit tested in
 * sales.test.ts (no component tests — codebase convention).
 */
import { useQuery } from '@tanstack/react-query';
import { ListSalesResponse, type SalesRow } from '@sourcito/schemas';
import { apiFetch } from './api';
import { money } from './price-calibration';

// ─────────────────────────────────────────────────────────
// Query
// ─────────────────────────────────────────────────────────

export interface SalesDateRange {
  /** YYYY-MM-DD */
  from: string;
  /** YYYY-MM-DD */
  to: string;
}

export function useSales(range: SalesDateRange, enabled = true) {
  return useQuery({
    queryKey: ['sales', range.from, range.to] as const,
    enabled,
    queryFn: () =>
      apiFetch(`/api/sales?from=${range.from}&to=${range.to}`, {
        responseSchema: ListSalesResponse,
      }),
  });
}

/** Last 90 days ending today, UTC-dated (matches the API's own default so
 *  the toolbar's initial inputs reflect exactly what an omitted range would
 *  have fetched). */
export function defaultSalesDateRange(now: Date = new Date()): SalesDateRange {
  const toDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const fromDate = new Date(toDate.getTime() - 90 * 24 * 60 * 60 * 1000);
  return { from: isoDate(fromDate), to: isoDate(toDate) };
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

// ─────────────────────────────────────────────────────────
// Row shape used by every pure helper below — a subset of SalesRow so
// tests can build fixtures without depending on every schema field.
// ─────────────────────────────────────────────────────────

export type SalesRowLike = SalesRow;

// ─────────────────────────────────────────────────────────
// Status pills — dynamically derived from whatever statuses are actually
// present in the fetched range (Faraz: "generate dynamically, don't
// hardcode a guess"). 'all' is always first with the total count.
// ─────────────────────────────────────────────────────────

export type SalesStatusFilter = 'all' | string;

export const DEFAULT_SALES_STATUS_FILTER: SalesStatusFilter = 'all';

export interface SalesStatusPill {
  key: SalesStatusFilter;
  label: string;
  count: number;
}

/** Pure and total: 'all' always present (count 0 for an empty list), one
 *  pill per distinct order_status, sorted alphabetically for a stable UI
 *  order regardless of fetch order. */
export function salesStatusPills(rows: Pick<SalesRowLike, 'orderStatus'>[]): SalesStatusPill[] {
  const counts = new Map<string, number>();
  for (const r of rows) {
    counts.set(r.orderStatus, (counts.get(r.orderStatus) ?? 0) + 1);
  }
  const statusPills = [...counts.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, count]) => ({ key, label: key, count }));
  return [{ key: 'all', label: 'All', count: rows.length }, ...statusPills];
}

export function matchesSalesStatusFilter(
  row: Pick<SalesRowLike, 'orderStatus'>,
  filter: SalesStatusFilter
): boolean {
  if (filter === 'all') return true;
  return row.orderStatus === filter;
}

export function filterSalesRowsByStatus<T extends Pick<SalesRowLike, 'orderStatus'>>(
  rows: T[],
  filter: SalesStatusFilter
): T[] {
  return rows.filter((r) => matchesSalesStatusFilter(r, filter));
}

// ─────────────────────────────────────────────────────────
// Fulfillment channel — 'AFN' (Amazon-fulfilled/FBA) | 'MFN'
// (merchant-fulfilled/FBM) | null (rows ingested before migration 0053).
// Pills dynamically derived from whatever channels are actually present in
// the fetched range, same pattern as salesStatusPills — never a hardcoded
// guess, and a null channel never gets a pill of its own (those rows just
// have no badge and match "All"). FBA is listed before FBM when both are
// present, matching Amazon's own AFN/MFN documentation order.
// ─────────────────────────────────────────────────────────

export type SalesChannelFilter = 'all' | 'AFN' | 'MFN';

export const DEFAULT_SALES_CHANNEL_FILTER: SalesChannelFilter = 'all';

const CHANNEL_LABELS: Record<'AFN' | 'MFN', string> = { AFN: 'FBA', MFN: 'FBM' };
const CHANNEL_ORDER: Array<'AFN' | 'MFN'> = ['AFN', 'MFN'];

export interface SalesChannelPill {
  key: SalesChannelFilter;
  label: string;
  count: number;
}

/** Pure and total: 'all' always present (count 0 for an empty list); an
 *  'AFN'/'MFN' pill only when that channel actually appears in `rows`. */
export function salesChannelPills(
  rows: Pick<SalesRowLike, 'fulfillmentChannel'>[]
): SalesChannelPill[] {
  const counts = new Map<'AFN' | 'MFN', number>();
  for (const r of rows) {
    if (r.fulfillmentChannel === 'AFN' || r.fulfillmentChannel === 'MFN') {
      counts.set(r.fulfillmentChannel, (counts.get(r.fulfillmentChannel) ?? 0) + 1);
    }
  }
  const channelPills = CHANNEL_ORDER.filter((c) => counts.has(c)).map((c) => ({
    key: c,
    label: CHANNEL_LABELS[c],
    count: counts.get(c)!,
  }));
  return [{ key: 'all', label: 'All', count: rows.length }, ...channelPills];
}

export function matchesSalesChannelFilter(
  row: Pick<SalesRowLike, 'fulfillmentChannel'>,
  filter: SalesChannelFilter
): boolean {
  if (filter === 'all') return true;
  return row.fulfillmentChannel === filter;
}

export function filterSalesRowsByChannel<T extends Pick<SalesRowLike, 'fulfillmentChannel'>>(
  rows: T[],
  filter: SalesChannelFilter
): T[] {
  return rows.filter((r) => matchesSalesChannelFilter(r, filter));
}

/** null for a null channel — the row renders with no badge, never a
 *  placeholder. FBA gets a neutral/positive read (the expected default);
 *  FBM gets a variant visually distinct from FBA per the brief ('accent' —
 *  the only Badge variant besides positive/negative/warning that isn't
 *  already used elsewhere on this table, e.g. the status badges). */
export function fulfillmentChannelBadge(
  channel: string | null
): { variant: SalesBadgeVariant | 'accent'; label: string } | null {
  if (channel === 'AFN') return { variant: 'neutral', label: 'FBA' };
  if (channel === 'MFN') return { variant: 'accent', label: 'FBM' };
  return null;
}

// ─────────────────────────────────────────────────────────
// Search — sku, product name, order id.
// ─────────────────────────────────────────────────────────

export function matchesSalesSearch(
  row: Pick<SalesRowLike, 'sellerSku' | 'productName' | 'amazonOrderId'>,
  query: string
): boolean {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return true;
  return (
    row.sellerSku.toLowerCase().includes(trimmed) ||
    (row.productName?.toLowerCase().includes(trimmed) ?? false) ||
    row.amazonOrderId.toLowerCase().includes(trimmed)
  );
}

export function searchSalesRows<T extends Parameters<typeof matchesSalesSearch>[0]>(
  rows: T[],
  query: string
): T[] {
  return rows.filter((r) => matchesSalesSearch(r, query));
}

// ─────────────────────────────────────────────────────────
// Sort — every column sortable; default is date desc (most recent first).
// Numeric columns that can be null (unitPrice/total) sort nulls-last
// regardless of direction, same convention as lib/price-calibration.ts.
// ─────────────────────────────────────────────────────────

export type SalesSortKey =
  | 'date'
  | 'orderId'
  | 'product'
  | 'qty'
  | 'unitPrice'
  | 'total'
  | 'status';
export type SalesSortDirection = 'asc' | 'desc';

function sortProductName(row: Pick<SalesRowLike, 'sellerSku' | 'productName'>): string {
  return row.productName ?? row.sellerSku;
}

function compareNullsLast(
  a: number | null,
  b: number | null,
  direction: SalesSortDirection
): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  const cmp = a - b;
  return direction === 'asc' ? cmp : -cmp;
}

export function compareSalesRows(
  a: SalesRowLike,
  b: SalesRowLike,
  key: SalesSortKey,
  direction: SalesSortDirection
): number {
  switch (key) {
    case 'date': {
      const cmp = Date.parse(a.purchaseDate) - Date.parse(b.purchaseDate);
      return direction === 'asc' ? cmp : -cmp;
    }
    case 'orderId': {
      const cmp = a.amazonOrderId.localeCompare(b.amazonOrderId);
      return direction === 'asc' ? cmp : -cmp;
    }
    case 'product': {
      const cmp = sortProductName(a).localeCompare(sortProductName(b));
      return direction === 'asc' ? cmp : -cmp;
    }
    case 'qty': {
      const cmp = a.qty - b.qty;
      return direction === 'asc' ? cmp : -cmp;
    }
    case 'unitPrice':
      return compareNullsLast(a.unitPriceCents, b.unitPriceCents, direction);
    case 'total':
      return compareNullsLast(a.rowTotalCents, b.rowTotalCents, direction);
    case 'status': {
      const cmp = a.orderStatus.localeCompare(b.orderStatus);
      return direction === 'asc' ? cmp : -cmp;
    }
  }
}

export function sortSalesRows(
  rows: SalesRowLike[],
  key: SalesSortKey,
  direction: SalesSortDirection
): SalesRowLike[] {
  return [...rows].sort((a, b) => compareSalesRows(a, b, key, direction));
}

// ─────────────────────────────────────────────────────────
// Status badge — maps free-text order_status to a Badge variant. Amazon's
// order-status vocabulary isn't a fixed enum in this table (it's whatever
// the flat-file report prints), so this matches by keyword rather than an
// exhaustive switch, and always falls back to a neutral badge carrying the
// raw label through rather than hiding an unrecognized status.
// ─────────────────────────────────────────────────────────

export type SalesBadgeVariant = 'neutral' | 'positive' | 'warning' | 'negative';

export interface SalesStatusBadge {
  variant: SalesBadgeVariant;
  label: string;
}

const POSITIVE_KEYWORDS = ['ship', 'deliver'];
const NEGATIVE_KEYWORDS = ['cancel', 'refund'];
const WARNING_KEYWORDS = ['pending', 'unshipped'];

export function statusBadge(status: string): SalesStatusBadge {
  const lower = status.toLowerCase();
  if (NEGATIVE_KEYWORDS.some((k) => lower.includes(k))) {
    return { variant: 'negative', label: status };
  }
  // Checked before the positive keywords: "Unshipped" contains "ship" as a
  // substring and must read as pending, not positive.
  if (WARNING_KEYWORDS.some((k) => lower.includes(k))) {
    return { variant: 'warning', label: status };
  }
  if (POSITIVE_KEYWORDS.some((k) => lower.includes(k))) {
    return { variant: 'positive', label: status };
  }
  return { variant: 'neutral', label: status };
}

// ─────────────────────────────────────────────────────────
// Totals strip — reflects the CURRENT filtered view (search + status +
// date range already applied by the caller before this runs).
// ─────────────────────────────────────────────────────────

export interface SalesTotals {
  orderCount: number;
  units: number;
  revenueCents: number;
}

/** Pure and total: all-zero for an empty list. orderCount counts DISTINCT
 *  amazon_order_id — one order can have several line items. */
export function salesTotals(
  rows: Pick<SalesRowLike, 'amazonOrderId' | 'qty' | 'rowTotalCents'>[]
): SalesTotals {
  const orderIds = new Set<string>();
  let units = 0;
  let revenueCents = 0;
  for (const r of rows) {
    orderIds.add(r.amazonOrderId);
    units += r.qty;
    revenueCents += r.rowTotalCents ?? 0;
  }
  return { orderCount: orderIds.size, units, revenueCents };
}

// ─────────────────────────────────────────────────────────
// Empty state / cap notice copy — plain direct sentences, never a bare
// empty table (Faraz brief), and honest truncation, never silent.
// ─────────────────────────────────────────────────────────

export function salesEmptyStateMessage(statusFilter: SalesStatusFilter, query: string): string {
  const trimmed = query.trim();
  if (trimmed) return `No sales match "${trimmed}".`;
  if (statusFilter === 'all') return 'No sales in this range.';
  return `No ${statusFilter.toLowerCase()} orders in this range.`;
}

export function capNoticeMessage(meta: { capped: boolean; rowCount: number }): string | null {
  if (!meta.capped) return null;
  return `Showing the most recent ${meta.rowCount.toLocaleString('en-US')} — narrow the date range.`;
}

// ─────────────────────────────────────────────────────────
// Formatting
// ─────────────────────────────────────────────────────────

export const unitPriceLabel = money;
export const totalLabel = money;
